/* eslint-disable angular/document-service */
import React, {
    useEffect, useState, useRef, Fragment,
} from 'react';
import PropTypes from 'prop-types';
import orderBy from 'lodash/orderBy';
import useClickOutside from './video-recorder-components/hooks/useClickOutside';
import Modal from './Modal';

const sortByOptions = [
    { label: 'Recently Updated', sortBy: 'updated_at', order: 'desc' },
    { label: 'Name', sortBy: 'name', order: 'asc' },
    { label: 'New to Old', sortBy: 'created_at', order: 'desc' },
    { label: 'Old to New', sortBy: 'created_at', order: 'asc' },
    { label: 'Most Contacts', sortBy: 'stats.numOfGuests', order: 'desc' },
    { label: 'Least Contacts', sortBy: 'stats.numOfGuests', order: 'asc' },
];

const ListsModal = ({
    isOpened = false, onCloseListDialog = () => { }, onAddFromLists = () => { },
}) => {
    const [lists, setLists] = useState([]);
    const [search, setSearch] = useState('');
    const [selectedList, setSelectedList] = useState(null);
    const [activeListSortBy, setActiveListSortBy] = useState(sortByOptions[0]);
    const [isSortByOpen, setIsSortByOpen] = useState(false);
    const [confirmList, setConfirmList] = useState(false);
    const dropdownRef = useRef();
    // Find the meta tag with name="csrf-token"
    const csrfMetaTag = document.querySelector('meta[name="csrf-token"]');
    // Extract the content attribute value (which is the CSRF token)
    const csrfToken = csrfMetaTag ? csrfMetaTag.getAttribute('content') : null;

    const handleSearchChange = (e) => {
        setSearch(e.target.value);
    };

    const handleSelectList = (e) => {
        if (selectedList) selectedList.selected = false;
        selectList(e.id);
    };

    const selectList = (newListId) => {
        if (!lists) return;
        const temp = lists.find((x) => x.id === newListId);
        if (temp) {
            temp.selected = true;
            setSelectedList(temp);
            setConfirmList(true);
        }
    };

    const fetchLists = () => {
        try {
            fetch('/api/lists')
                .then((response) => response.json())
                .then((data) => {
                    setLists(data.data.lists);
                })
                .catch((error) => console.log('ERROR: ', error));
        } catch (error) {
            console.error('Error fetching contacts:', error);
        }
    };

    const handleClose = () => {
        // reset to selected contact if user closes out of the modal
        if (selectedList) {
            selectedList.selected = false;
            selectList(null);
        }
        onCloseListDialog();
    };

    const selectSortBy = (e, val) => {
        setActiveListSortBy(val);
        setIsSortByOpen(false);
    };

    const toggleSortByDropdown = () => {
        setIsSortByOpen(!isSortByOpen);
    };

    const handleAddList = async () => {
        setConfirmList(false);
        handleClose();
        try {
            const params = {
                list_id: selectedList.id,
            };
            const response = await fetch('/api/personalVideo/list', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': csrfToken,
                },
                body: JSON.stringify(params),
            });

            if (response.ok) {
                const responseData = await response.json();
                const personalVideos = responseData?.data?.personalVideos;
                onAddFromLists(personalVideos);
            } else {
                throw new Error('Request failed with status ' + response.status);
            }
        } catch (error) {
            console.error('An error occurred:', error);
        }
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
        }
    };

    useEffect(() => {
        fetchLists();
    }, []);

    useClickOutside(dropdownRef, () => {
        setIsSortByOpen(false);
    });

    const filteredAndSortedLists = orderBy(
        lists.filter((list) => list.name.toLowerCase().includes(search.toLowerCase())),
        [activeListSortBy.sortBy],
        [activeListSortBy.order],
    );

    return (
        isOpened && (
            <div id='popupContainer' className='tv-overlay overlay-form pv-contact-overlay overlay-guest-add'>
                <div
                    id='backdrop'
                    className='tv-overlay__backdrop'
                    data-a11y-dialog-hide='true'
                ></div>
                <div className='tv-overlay__content-wrapper' role='document'>
                    <div className='tv-overlay__header'>
                        <button
                            className='btn btn--ghost btn--close'
                            onClick={handleClose}
                            aria-label='Close dialog'
                        >
                            <svg
                                className='tv-icon close'
                                xmlns='http://www.w3.org/2000/svg'
                                viewBox='0 0 24 24'
                                aria-hidden="true"
                            >
                                <path
                                    d='M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z' />
                                <path d='M0 0h24v24H0z' fill='none' />
                            </svg>
                        </button>
                    </div>
                    <div className='tv-overlay__content guest-select font-size-16px'>
                        <h2 className="margin-b-8px font-size-24px">Add from Lists</h2>
                        <p className="font-size-16px margin-t-8px margin-b-8px">
                            Import contacts from a specific list.
                        </p>
                        <div className="control-area flex align-items-center justify-content-space-around argin-l-auto margin-r-auto margin-b-8px">
                            <span className="inline-block v-align-middle">Sort By: </span>
                            <div className="dropdown" ref={dropdownRef}>
                                <button className="dropdown__toggle" onClick={toggleSortByDropdown}>
                                    {activeListSortBy.label} <i className={`dropdown__toggle-indicator fa ${isSortByOpen ? 'fa-angle-up' : 'fa-angle-down'}`}></i>
                                </button>
                                {
                                    <ul className="dropdown__menu" style={{
                                        display: isSortByOpen ? 'block' : 'none',
                                    }}>
                                        {
                                            sortByOptions.map((sortBy) => (
                                                <Fragment key={sortBy.label}>
                                                    {
                                                        sortBy.label !== activeListSortBy.label && (
                                                            <li className={`dropdown__menu-item ${sortBy.label === activeListSortBy.label ? 'selected' : ''}`} onClick={(e) => selectSortBy(e, sortBy)}>
                                                                {sortBy.label}
                                                            </li>
                                                        )
                                                    }
                                                </Fragment>
                                            ))
                                        }
                                    </ul>
                                }
                            </div>
                            <div className="search-field">
                                <form>
                                    <label htmlFor="guest-add-dialog-list-search" className="sr-only">Search for a List</label>
                                    <i className="fa fa-search"></i>
                                    <input id="guest-add-dialog-list-search"
                                        placeholder="Search for a List"
                                        value={search}
                                        onChange={handleSearchChange}
                                        onKeyDown={handleKeyDown}
                                    />
                                </form>
                            </div>
                        </div>

                        <div id="pv-contacts-list" className="guest-select-list margin-center">
                            {filteredAndSortedLists.length > 0 && filteredAndSortedLists.map((list) => (
                                <Fragment key={list.id} >
                                    {
                                        <div className={`guest-item text-align-left ${list.selected ? 'selected' : ''} ${list.stats.numOfGuests === 0 || !list.stats['email' + 'Guests'] ? 'disabled' : ''}`} onClick={() => handleSelectList(list)} style={{ borderBottom: filteredAndSortedLists.length > 0 ? 'inherit' : 'none' }}>
                                            <span className="list-title font-size-16px md-font-size-18px inline-block width-67 md-width-75 overflow-hidden text-overflow-ellipsis v-align-middle margin-t-0px margin-b-0px">{list.name}</span>
                                            <p className="contact-count font-size-16px inline-block width-33 md-width-25 text-overflow-ellipsis v-align-middle margin-t-0px margin-b-0px">
                                                <span>{list.stats['email' + 'Guests'] || 0} of {list.stats.numOfGuests}</span>
                                                <br /> Contacts <br className="sm-none" />&nbsp;w/<span className="text-capitalize">email</span>
                                            </p>
                                        </div>
                                    }
                                </Fragment>

                            ))}

                            {
                                lists.length === 0 && <p>You have no lists that can be added.</p>
                            }
                        </div>
                    </div>
                </div>

                <Modal
                    id='confirm-list'
                    isOpened={confirmList}
                    ctaClick={handleAddList}
                    onCloseDialog={() => setConfirmList(false)}
                    title="Are you sure you want to add this list?"
                    description={`This will add ${selectedList?.donor_id_count ?? 0} out of ${selectedList?.stats?.numOfGuests} contacts to your video list.`}
                    callout="Note: Contacts must have a valid Donor ID attached before they can be imported into the video list."
                    cancelBtnTxt="Go Back"
                    continueBtnTxt="Confirm"
                />
            </div>
        ));
};

ListsModal.propTypes = {
    isOpened: PropTypes.bool,
    onCloseListDialog: PropTypes.func,
    onAddFromLists: PropTypes.func,
};

export default ListsModal;
